// A folha de laboratório da cabeceira — só desenho.
//
// Pedido do dono em 25/09: uma folha impressa ao lado da cama para o médico que
// passa rápido e pergunta como estão os exames. Curta de propósito: resumo,
// tabela com a curva de cada marcador, o que vigiar e as correlações.
//
// Os números e as curvas vêm do banco na hora; a leitura é texto escrito à
// parte, e a folha diz de quando ela é. Sem busca e sem estado: `Relatorio.tsx`
// traz os dados, e `conferir-relatorio-laboratorio.mts` renderiza esta folha
// com dados reais para olhar antes de publicar.
import type { KeyboardEvent } from 'react';
import { formatarData, formatarDataHora, formatarDiaMes } from '@medicov1/shared';
import { BlocoMd, Texto } from './RelatorioEspecial';
import { identificar } from './identificacao';
import {
  SETA, ROTULO_SITUACAO, geometriaDaCurva, dividirLeitura,
  type FolhaDeLaboratorioDTO, type LinhaDTO,
} from './relatorioLaboratorioTipos';

export type { FolhaDeLaboratorioDTO } from './relatorioLaboratorioTipos';

const LARGURA = 120;
const ALTURA = 30;

function Curva({ l }: { l: LinhaDTO }) {
  const { pontos, faixa } = geometriaDaCurva(l.serie, l.referencia, LARGURA, ALTURA);
  const ultimo = pontos[pontos.length - 1];
  return (
    <svg className="flab__curva" viewBox={`-3 -3 ${LARGURA + 6} ${ALTURA + 6}`} role="img"
      aria-label={`${l.rotulo}: ${l.serie.map((p) => p.valor).join(', ')}`}>
      {faixa && <rect x={0} y={faixa.y} width={LARGURA} height={faixa.h} className="flab__faixa" />}
      {pontos.length > 1 && (
        <polyline points={pontos.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')} className="flab__linha" />
      )}
      {pontos.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={1.6} className="flab__ponto" />)}
      {ultimo && <circle cx={ultimo.x} cy={ultimo.y} r={3} className={`flab__fim flab__fim--${l.situacao}`} />}
    </svg>
  );
}

/**
 * `aoAbrirMarcador`: com ele, cada exame vira porta para a história dele na
 * internação inteira (27/09). Sem ele — a folha renderizada para conferir,
 * ou para o papel — as linhas não reagem a toque.
 */
export function FolhaLaboratorio({ dados, aoAbrirMarcador }: { dados: FolhaDeLaboratorioDTO; aoAbrirMarcador?: (chave: string) => void }) {
  // Folha da cabeceira, para o médico: identifica como o hospital.
  const quem = identificar(dados.paciente, true);
  const leitura = dados.leitura ? dividirLeitura(dados.leitura.texto) : null;

  return (
    <article className="doc doc--lab">
      <header className="doc__cabeca">
        <div>
          <h1>{quem.nome}</h1>
          <p className="doc__quem">{quem.linha}</p>
        </div>
        <div className="doc__selo">
          <span>Exames de laboratório</span>
          {dados.dia
            ? <small>coleta de {formatarData(`${dados.dia}`)}</small>
            : <small>números de {formatarDataHora(new Date(dados.gerado.em))}</small>}
          {dados.leitura && <small>leitura de {formatarDataHora(new Date(dados.leitura.geradoEm))}</small>}
        </div>
      </header>

      {leitura?.resumo && (
        <div className="flab__resumo">
          <b>Em resumo</b>
          <BlocoMd b={leitura.resumo} />
        </div>
      )}

      {/* ---------------------------------------------------------- a tabela */}
      <section className="doc__bloco">
        <h2>
          {dados.dia ? `Exames de ${formatarData(dados.dia)}` : 'Como estão'}{' '}
          <small>{dados.dia ? '"antes" = a coleta anterior · curva das 3 semanas até o dia' : 'curva das últimas 3 semanas'} · faixa verde = normal</small>
        </h2>
        <div className="doc__rolagem">
          <table className="doc__tabela flab__tabela">
            <thead>
              <tr><th>Exame</th><th className="num">{dados.dia ? 'No dia' : 'Último'}</th><th className="num">Antes</th><th>Curva</th></tr>
            </thead>
            <tbody>
              {dados.grupos.map((g) => [
                <tr key={g.rotulo} className="flab__grupo"><td colSpan={4}>{g.rotulo}</td></tr>,
                ...g.linhas.map((l) => (
                  <tr key={l.chave} className={`flab__linha--${l.situacao}${aoAbrirMarcador ? ' flab__tocavel' : ''}`}
                    {...(aoAbrirMarcador ? {
                      role: 'button', tabIndex: 0, 'aria-label': `${l.rotulo}: ver a história na internação`,
                      onClick: () => aoAbrirMarcador(l.chave),
                      onKeyDown: (e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); aoAbrirMarcador(l.chave); } },
                    } : {})}>
                    <td>
                      <b>{l.rotulo}</b>
                      {aoAbrirMarcador && <small className="flab__abrir">ver a história ›</small>}
                      {l.referencia && (
                        <small className="flab__ref">{l.referencia.min.toLocaleString('pt-BR')}–{l.referencia.max.toLocaleString('pt-BR')} {l.unidade}</small>
                      )}
                    </td>
                    <td className="num">
                      <b className={`flab__valor flab__valor--${l.situacao}`}>
                        {l.ultimo.texto}{l.direcao && <span className="flab__seta"> {SETA[l.direcao]}</span>}
                      </b>
                      <small>{formatarDiaMes(new Date(l.ultimo.em))}{l.diasDesde > 3 && <> · há {l.diasDesde} d</>}</small>
                      {ROTULO_SITUACAO[l.situacao] && l.situacao !== 'dentro' && (
                        <small className={`flab__sit flab__sit--${l.situacao}`}>{ROTULO_SITUACAO[l.situacao]}</small>
                      )}
                    </td>
                    <td className="num flab__antes">
                      {l.anterior ? <>{l.anterior.texto}<small>{formatarDiaMes(new Date(l.anterior.em))}</small></> : '—'}
                    </td>
                    <td><Curva l={l} /></td>
                  </tr>
                )),
              ])}
            </tbody>
          </table>
        </div>
      </section>

      {/* -------------------------------------------------------- culturas */}
      {/* Até 30/09 só as culturas SEM resultado apareciam, em "Vigiar": a
          urocultura com Klebsiella KPC + NDM sumiu da folha no dia em que saiu.
          Agora toda cultura aparece, com o resultado e o que ele quer dizer. */}
      {dados.culturas && dados.culturas.length > 0 && (
        <section className="doc__bloco">
          <h2>Culturas</h2>
          <ul className="flab__culturas">
            {dados.culturas.map((c, i) => (
              <li key={i} className={`flab__cultura flab__cultura--${c.peso}`}>
                <div className="flab__cultura-topo">
                  <b>{c.material}</b>
                  <span className="flab__cultura-data">colhida em {formatarData(c.coletadoEm)}</span>
                </div>
                <span className="flab__cultura-situacao">{c.situacao}</span>
                {c.resultado && <p className="flab__cultura-resultado">{c.resultado}</p>}
                {c.sensibilidade && <p className="flab__cultura-resultado"><b>Sensível a:</b> {c.sensibilidade}</p>}
                <details className="flab__cultura-explica">
                  <summary>O que este resultado quer dizer</summary>
                  <p>{c.explicacao}</p>
                  <p><b>Pergunta para a equipe:</b> {c.pergunta}</p>
                </details>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ---------------------------------------------------------- vigiar */}
      {dados.vigiar.length > 0 && (
        <section className="doc__bloco doc__bloco--perguntas">
          <h2>Vigiar</h2>
          <ul className="flab__vigiar">
            {dados.vigiar.map((v, i) => <li key={i} className={`flab__vigiar--${v.tipo}`}>{v.texto}</li>)}
          </ul>
        </section>
      )}

      {/* ------------------------------------------------------- leitura */}
      {leitura && leitura.resto.length > 0 && (
        <section className="doc__bloco flab__leitura">
          <h2>Leitura do conjunto <small>{dados.leitura!.autor} · {formatarData(new Date(dados.leitura!.geradoEm))}</small></h2>
          {leitura.resto.map((b, i) => <BlocoMd b={b} key={i} />)}
        </section>
      )}

      <footer className="doc__rodape">
        <p className="fraco">
          Registro de apoio da família, feito a partir dos laudos do laboratório do hospital. <Texto t={[{ texto: 'Não é diagnóstico', negrito: true }]} />:
          a leitura do conjunto é da equipe.
        </p>
      </footer>
    </article>
  );
}
